import React from 'react';
import { TextInput, Textarea, Select, ChipSelector } from '@parsina/framework';

import { useTranslation } from '../../i18n/I18nProvider.jsx';
import {
  SEXES, STATUSES, BLOOD_TYPES, emptyCaseForm, titleProblem,
} from '../../lib/caseForm.js';
import { PracticeBanner } from './PracticeNotice.jsx';

// The بیمار تمرینی form — one component, two callers.
//
// «مورد تازه» on the case list creates through it; «ویرایش مورد» on the case
// record edits through it. Two forms would drift the day a field is added to one
// of them, and the field most likely to be added is the one this app must never
// have (CORE.md §3) — so there is one place to look.
//
// It is CONTROLLED and does not submit: the caller owns the values, decides
// whether to send a create input or a changed-fields patch (src/lib/caseForm.js),
// and renders its own commit row. That is what lets the create dialog and the
// edit drawer differ in chrome while agreeing, field for field, about the case.
//
// ── The banner is inside the form, not above it ──────────────────────────
//
// This is where a student types a name and a label, and it is the one moment
// they could type a real person's. The reminder belongs in the same reading
// column as the fields, before them.

/**
 * @param values    the form object from src/lib/caseForm.js
 * @param onChange  (patch) => void — merged over `values` by the caller
 * @param showBanner  false inside a surface that already carries the banner
 * @param showStatus  false on create, where every case starts `active`
 */
export default function CaseForm({
  values = emptyCaseForm(),
  onChange,
  disabled = false,
  showBanner = true,
  showStatus = true,
  showTitleError = false,
}) {
  const { t } = useTranslation();
  const set = (key) => (e) => onChange?.({ [key]: e?.target ? e.target.value : e });

  const titleError = showTitleError ? titleProblem(values) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {showBanner && <PracticeBanner />}

      <TextInput
        label={t('caseForm.fields.title')}
        required
        value={values.title}
        onChange={set('title')}
        placeholder={t('caseForm.fields.titlePlaceholder')}
        helperText={t('caseForm.fields.titleHelper')}
        error={titleError ? t(titleError) : ''}
        disabled={disabled}
        maxLength={200}
        data-testid="case-title"
      />

      <TextInput
        label={t('caseForm.fields.displayName')}
        value={values.displayName}
        onChange={set('displayName')}
        placeholder={t('caseForm.fields.displayNamePlaceholder')}
        helperText={t('caseForm.fields.displayNameHelper')}
        disabled={disabled}
        maxLength={120}
        data-testid="case-display-name"
      />

      {/*
        THE STICKY NOTE. It is the student's own filing label — «مورد ۱۲»,
        «CASE-2026-03» — and it is NOT a کد ملی, not a شمارهٔ پرونده, and not any
        identifier belonging to a human being (CORE.md §4).

        So: it is never rendered under a «کد ملی» label, it is never validated as
        one (validating it is what teaches a student that it is one), and there is
        no `NationalIdInput` on this form even though the kit ships one. `dir` is
        left to the field's own default rather than forced LTR — a Persian label
        like «کارآموزی داخلی ۷» is at least as likely as a Latin one.
      */}
      <TextInput
        label={t('practice.caseRef.label')}
        value={values.caseRef}
        onChange={set('caseRef')}
        helperText={t('practice.caseRef.helper')}
        disabled={disabled}
        maxLength={64}
        data-testid="case-ref"
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
        <TextInput
          label={t('caseForm.fields.ageYears')}
          type="number"
          value={values.ageYears}
          onChange={set('ageYears')}
          disabled={disabled}
          dir="ltr"
          inputMode="numeric"
          data-testid="case-age-years"
        />
        <TextInput
          label={t('caseForm.fields.ageDisplay')}
          value={values.ageDisplay}
          onChange={set('ageDisplay')}
          placeholder={t('caseForm.fields.ageDisplayPlaceholder')}
          helperText={t('caseForm.fields.ageDisplayHelper')}
          disabled={disabled}
          maxLength={40}
          data-testid="case-age-display"
        />
      </div>

      {/* A three-value closed set is a chip row, not a dropdown: every option
          fits on one line and a select would hide two of the three behind a tap. */}
      <ChipSelector
        label={t('caseForm.fields.sex')}
        value={values.sex}
        onChange={(next) => onChange?.({ sex: next })}
        options={SEXES.map((id) => ({ value: id, label: t(`common.gender.${id}`) }))}
        disabled={disabled}
      />

      {/*
        Blood type, weight and height — the three v2 additions, and the shape of
        the line this form does NOT cross. They are CLINICAL facts about an
        invented patient, the kind a student needs to dose a drug or read a lab
        value. They are not identity: none of them names a person, none of them
        can be matched against a registry, and adding them does not make the
        next field (a birth date, a phone number) any more thinkable — core.md
        §3.1 keeps those ABSENT from the table, the proto, the type and this
        form.
      */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
        <Select
          label={t('caseForm.fields.bloodType')}
          value={values.bloodType}
          onChange={set('bloodType')}
          options={BLOOD_TYPES.map((id) => ({ value: id, label: id }))}
          placeholder={t('common.labels.none')}
          disabled={disabled}
          data-testid="case-blood-type"
        />
        <TextInput
          label={t('caseForm.fields.weightKg')}
          value={values.weightKg}
          onChange={set('weightKg')}
          disabled={disabled}
          dir="ltr"
          inputMode="decimal"
          data-testid="case-weight"
        />
        <TextInput
          label={t('caseForm.fields.heightCm')}
          value={values.heightCm}
          onChange={set('heightCm')}
          disabled={disabled}
          dir="ltr"
          inputMode="decimal"
          data-testid="case-height"
        />
      </div>

      <TextInput
        label={t('caseForm.fields.chiefComplaint')}
        value={values.chiefComplaint}
        onChange={set('chiefComplaint')}
        placeholder={t('caseForm.fields.chiefComplaintPlaceholder')}
        helperText={t('caseForm.fields.chiefComplaintHelper')}
        disabled={disabled}
        maxLength={300}
        data-testid="case-chief-complaint"
      />

      {/* Status is absent on CREATE. Every case starts `active` — the contract's
          own default — and asking a student to classify a case they have not
          written yet is a decision with no information behind it. */}
      {showStatus && (
        <Select
          label={t('caseForm.fields.status')}
          value={values.status}
          onChange={set('status')}
          options={STATUSES.map((id) => ({ value: id, label: t(`patients.status.${id}`) }))}
          placeholder=""
          disabled={disabled}
          data-testid="case-status"
        />
      )}

      <Textarea
        label={t('caseForm.fields.note')}
        value={values.note}
        onChange={set('note')}
        rows={3}
        disabled={disabled}
        maxLength={1000}
        data-testid="case-note"
      />
    </div>
  );
}
